import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Trash2, Plus, UserPlus } from "lucide-react";
import { api, eur, errMsg, today } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import { PageHeader, Panel, Field } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScanButton } from "@/components/BarcodeScanner";
import { CustomerDialog, EMPTY_CUSTOMER } from "@/components/QuickDialogs";
import { OrdineCassa } from "@/components/OrdineCassa";
import { DateField } from "@/components/DateField";

const nomeCli = (c) => c.denominazione || `${c.nome || ""} ${c.cognome || ""}`.trim();
const fromProd = (p) => ({ product_id: p.id, codice: p.codice || "", descrizione: p.descrizione, quantita: 1, unita: p.unita || "", prezzo: p.prezzo || 0, sconto: 0, aliquota: p.aliquota ?? 22, natura: p.natura || "" });
const EMPTY_LINE = { product_id: "", codice: "", descrizione: "", quantita: 1, unita: "", prezzo: 0, sconto: 0, aliquota: 22, natura: "" };

const Cerca = ({ products, onPick }) => {
  const [q, setQ] = useState("");
  const hits = q.length < 2 ? [] : products.filter((p) => `${p.codice} ${p.descrizione} ${p.barcode || ""}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  return (
    <div className="relative flex-1">
      <Input data-testid="order-product-search" className="h-11 bg-white" placeholder="Cerca articolo per codice, descrizione o codice a barre" value={q} onChange={(e) => setQ(e.target.value)} />
      {hits.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          {hits.map((p) => (
            <button key={p.id} type="button" data-testid={`order-product-hit-${p.id}`} onClick={() => { onPick(p); setQ(""); }} className="flex w-full justify-between px-3 py-2.5 text-left text-sm hover:bg-sky-50">
              <span><b className="font-mono">{p.codice}</b> {p.descrizione}</span><span className="font-mono">{eur(p.prezzo)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Riga = ({ ln, i, set, del, ro }) => (
  <div className="grid grid-cols-12 items-end gap-2 border-b border-slate-100 py-3" data-testid={`order-line-${i}`}>
    <Field label="Descrizione" className="col-span-12 lg:col-span-5"><Input disabled={ro} value={ln.descrizione} data-testid={`order-line-desc-${i}`} onChange={(e) => set({ ...ln, descrizione: e.target.value })} /></Field>
    <Field label="Q.tà" className="col-span-3 lg:col-span-2"><Input disabled={ro} type="number" min="0" step="any" className="h-11 text-lg" value={ln.quantita} data-testid={`order-line-qty-${i}`} onChange={(e) => set({ ...ln, quantita: Number(e.target.value) })} /></Field>
    <Field label="Prezzo" className="col-span-3 lg:col-span-2"><Input disabled={ro} type="number" step="0.01" value={ln.prezzo} data-testid={`order-line-price-${i}`} onChange={(e) => set({ ...ln, prezzo: Number(e.target.value) })} /></Field>
    <Field label="Sconto" className="col-span-2 lg:col-span-1"><Input disabled={ro} type="number" step="any" value={ln.sconto} onChange={(e) => set({ ...ln, sconto: Number(e.target.value) })} /></Field>
    <div className="col-span-3 whitespace-nowrap pb-2.5 text-right font-mono text-sm lg:col-span-1">{eur(ln.quantita * ln.prezzo * (1 - (ln.sconto || 0) / 100))}</div>
    {!ro && <Button variant="ghost" size="icon" className="col-span-1 justify-self-end" data-testid={`order-line-remove-${i}`} onClick={del}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
  </div>
);

const KIND = {
  ordini: { api: "/ordini", back: "/app/ordini", one: "Ordine", nuovo: "Nuovo ordine", locked: "Ordine evaso: solo consultazione" },
  preventivi: { api: "/preventivi", back: "/app/preventivi", one: "Preventivo", nuovo: "Nuovo preventivo", locked: "Preventivo già trasformato: solo consultazione" },
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

export default function OrdineEditor({ kind = "ordini" }) {
  const K = KIND[kind];
  const prev = kind === "preventivi";
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState(id ? null : { data: today(), validita: prev ? addDays(today(), 30) : "", customer_id: "", lines: [], note: "", consegna: "" });
  const [custs, setCusts] = useState([]);
  const [products, setProducts] = useState([]);
  const [newCust, setNewCust] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/customers").then((r) => setCusts(r.data.filter((c) => c.tipo !== "fornitore")));
    api.get("/products").then((r) => setProducts(r.data));
    if (id) api.get(`${K.api}/${id}`).then((r) => setF(r.data)).catch(() => nav(K.back));
  }, [id, nav, K]);
  if (!f) return null;
  const ro = !canWrite(user) || (prev ? !!(f.ordine_id || f.invoice_id || f.ddt_id) : f.stato && f.stato !== "aperto");
  const addLine = (ln) => setF((s) => {
    const k = ln.product_id && s.lines.findIndex((x) => x.product_id === ln.product_id && x.prezzo === ln.prezzo);
    if (k !== "" && k >= 0) return { ...s, lines: s.lines.map((x, j) => (j === k ? { ...x, quantita: x.quantita + (ln.quantita || 1) } : x)) };
    return { ...s, lines: [...s.lines, ln] };
  });
  const scan = (code) => {
    const p = products.find((x) => x.barcode === code || x.codice === code);
    if (!p) return toast.error(`Nessun articolo con codice ${code}`);
    addLine(fromProd(p)); toast.success(`${p.descrizione} aggiunto`);
  };
  const imp = f.lines.reduce((s, l) => s + l.quantita * l.prezzo * (1 - (l.sconto || 0) / 100), 0);
  const iva = f.lines.reduce((s, l) => s + l.quantita * l.prezzo * (1 - (l.sconto || 0) / 100) * (l.aliquota || 0) / 100, 0);
  const save = async () => {
    setBusy(true);
    const body = { data: f.data, customer_id: f.customer_id, lines: f.lines, note: f.note, consegna: f.consegna, ...(prev ? { validita: f.validita || "" } : {}) };
    try {
      const { data } = id ? await api.put(`${K.api}/${id}`, body) : await api.post(K.api, body);
      toast.success(`${K.one} ${data.numero} salvato`); nav(K.back);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Eliminare questo ${K.one.toLowerCase()}?`)) return;
    try { await api.delete(`${K.api}/${id}`); toast.success(`${K.one} eliminato`); nav(K.back); } catch (e) { toast.error(errMsg(e)); }
  };
  const custDialog = <CustomerDialog value={newCust} onClose={() => setNewCust(null)} onSaved={(c) => { setNewCust(null); setCusts((s) => [...s, c]); setF((s) => ({ ...s, customer_id: c.id })); }} />;
  if (kind === "ordini" && user.tenant_role === "agente") {
    return (
      <div>
        <PageHeader title={id ? `${K.one} ${f.numero}` : K.nuovo} subtitle={ro ? K.locked : "Tocca gli articoli o usa il tastierino come alla cassa"}>
          {id && !ro && <Button variant="outline" className="bg-white text-rose-700" data-testid="order-delete-button" onClick={del}><Trash2 className="mr-2 h-4 w-4" />Elimina</Button>}
        </PageHeader>
        <OrdineCassa f={f} setF={setF} custs={custs} products={products} ro={ro} busy={busy} save={save} addLine={addLine} scan={scan} onNewCust={() => setNewCust({ ...EMPTY_CUSTOMER })} />
        {custDialog}
      </div>
    );
  }
  return (
    <div>
      <PageHeader title={id ? `${K.one} ${f.numero}` : K.nuovo} subtitle={ro ? K.locked : "Scansiona o cerca gli articoli e indica le quantità"}>
        {id && !ro && <Button variant="outline" className="bg-white text-rose-700" data-testid="order-delete-button" onClick={del}><Trash2 className="mr-2 h-4 w-4" />Elimina</Button>}
        {!ro && <Button className="bg-sky-600 hover:bg-sky-700" disabled={busy} data-testid="order-save-button" onClick={save}><Save className="mr-2 h-4 w-4" />Salva {K.one.toLowerCase()}</Button>}
      </PageHeader>
      <Panel title="Cliente" className="mb-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Cliente" className="sm:col-span-2">
            <div className="flex gap-2">
              <Select disabled={ro} value={f.customer_id || undefined} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                <SelectTrigger data-testid="order-customer-select" className="h-11"><SelectValue placeholder="Scegli il cliente" /></SelectTrigger>
                <SelectContent>{custs.map((c) => <SelectItem key={c.id} value={c.id}>{nomeCli(c)}</SelectItem>)}</SelectContent>
              </Select>
              {!ro && <Button variant="outline" className="h-11 shrink-0" data-testid="order-new-customer-button" onClick={() => setNewCust({ ...EMPTY_CUSTOMER })}><UserPlus className="mr-1 h-4 w-4" />Nuovo</Button>}
            </div>
          </Field>
          <Field label="Data"><DateField disabled={ro} className="h-11" testid="ordine-date-input" value={f.data} onChange={(v) => setF({ ...f, data: v })} /></Field>
          {prev && <Field label="Valido fino al"><DateField clearable disabled={ro} className="h-11" testid="quote-validity-input" value={f.validita || ""} onChange={(v) => setF({ ...f, validita: v })} /></Field>}
          <Field label="Consegna presso"><Input disabled={ro} className="h-11" placeholder="Facoltativo" value={f.consegna} data-testid="order-delivery-input" onChange={(e) => setF({ ...f, consegna: e.target.value })} /></Field>
        </div>
      </Panel>
      <Panel title="Articoli">
        {!ro && (
          <div className="mb-2 flex flex-col gap-2 sm:flex-row">
            <ScanButton onCode={scan} testid="order-scan-button" />
            <Cerca products={products} onPick={(p) => addLine(fromProd(p))} />
            <Button variant="outline" className="h-11" data-testid="order-add-free-line" onClick={() => addLine({ ...EMPTY_LINE })}><Plus className="mr-1 h-4 w-4" />Riga libera</Button>
          </div>
        )}
        {f.lines.length === 0 ? <p className="py-6 text-center text-sm text-slate-500">Nessun articolo: scansiona o cerca per aggiungerlo</p>
          : f.lines.map((ln, i) => <Riga key={i} ln={ln} i={i} ro={ro} set={(v) => setF({ ...f, lines: f.lines.map((x, j) => (j === i ? v : x)) })} del={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })} />)}
        <Field label={prev ? "Note per il cliente" : "Note per l'ufficio"} className="mt-4"><Input disabled={ro} value={f.note} data-testid="order-notes-input" onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <div className="mt-4 flex justify-end gap-6 text-sm" data-testid="order-totals">
          <span>Imponibile <b className="font-mono">{eur(imp)}</b></span><span>IVA <b className="font-mono">{eur(iva)}</b></span>
          <span className="text-base">Totale <b className="font-mono">{eur(imp + iva)}</b></span>
        </div>
      </Panel>
      {custDialog}
    </div>
  );
}
